import { useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import PeopleIcon from '@mui/icons-material/BadgeOutlined';
import TestsIcon from '@mui/icons-material/FactCheckOutlined';
import LogoutIcon from '@mui/icons-material/LogoutOutlined';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import { useAuth } from '../contexts/AuthContext';
import { downloadStationUtility } from '../lib/api';

const DRAWER_WIDTH = 232;

const NAV = [
  { to: '/tests', label: 'Tests', icon: <TestsIcon /> },
  { to: '/users', label: 'Users', icon: <PeopleIcon /> },
];

export function Layout({ children }: { children: ReactNode }) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  const download = async () => {
    setDownloading(true);
    setDownloadFailed(false);
    try {
      await downloadStationUtility();
    } catch {
      setDownloadFailed(true);
    } finally {
      setDownloading(false);
    }
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar>
        <Box component="img" src="/semmco-logo.svg" alt="Semmco Life Protection Systems" sx={{ height: 40 }} />
      </Toolbar>
      <Divider />
      <List sx={{ px: 1, flex: 1 }}>
        {NAV.map((item) => (
          <ListItemButton
            key={item.to}
            component={NavLink}
            to={item.to}
            selected={location.pathname.startsWith(item.to)}
            onClick={() => setMobileOpen(false)}
            sx={{ borderRadius: 1, mb: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItemButton>
        ))}
      </List>
      <List sx={{ px: 1 }}>
        <ListItemButton onClick={() => void download()} disabled={downloading} sx={{ borderRadius: 1 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <DownloadIcon />
          </ListItemIcon>
          <ListItemText
            primary={downloading ? 'Downloading…' : 'Download utility'}
            secondary={downloadFailed ? 'Download failed' : 'Windows exe + readme'}
            slotProps={{ secondary: { color: downloadFailed ? 'error' : 'text.secondary' } }}
          />
        </ListItemButton>
      </List>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" noWrap title={user?.email || user?.name}>
          {user?.name || user?.email}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {user?.roles.join(', ')}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{ width: { md: `calc(100% - ${DRAWER_WIDTH}px)` }, ml: { md: `${DRAWER_WIDTH}px` } }}
      >
        <Toolbar>
          {!isDesktop && (
            <IconButton edge="start" onClick={() => setMobileOpen(true)} sx={{ mr: 1 }} aria-label="Open menu">
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ flex: 1 }}>
            EEBD
          </Typography>
          <Tooltip title="Sign out">
            <IconButton onClick={() => void logout()} aria-label="Sign out">
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        <Drawer
          variant={isDesktop ? 'permanent' : 'temporary'}
          open={isDesktop || mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' } }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: { xs: 2, md: 3 } }}>
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
}
